import React, { useState, useCallback, useEffect } from 'react';
import axios from '../../axiosInstance';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import FilterTags1 from '../../components/FilterTags1';
import Pagination from '../../components/Pagination';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha'; // For Alphabetical Sort
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import ArticleIcon from '@mui/icons-material/Article';
import FacetArticles from '../../components/FacetArticles';
import defaultThumbnail from '../../assets/eBraryIcon.png';
import { useTheme } from '@mui/material/styles';
import SearchBox_ebrary from '../../components/SearchBox_ebrary_journalarticles';
import ReactGA from 'react-ga';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';


import {
  Typography,
  Box,
  CircularProgress,
  Snackbar,
  Modal,
  Paper,
  IconButton, 
  Tabs,
  Tab,
  useMediaQuery,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { styled } from '@mui/system';
import PropTypes from 'prop-types';



// Register the required components
ChartJS.register(ArcElement, Tooltip, Legend);  



// Function to convert search results to CSV format
const convertResultsToCSV = (results) => {
  const headers = ['Title', 'Publisher', 'ISSN', 'Total Articles', 'Impact Factor', 'H-Index', 'i10 Index', 'Open Access', 'Source'];
  const rows = results.map(result => [
    `"${result.title}"`,
    `"${result.publisher}"`,
    `"${result.issn}"`,
    `"${result.totalWorks}"`,
    `"${result.impactFactor}"`,
    `"${result.hIndex}"`,
    `"${result.i10Index}"`,
    `"${result.isOA}"`,
    `"${result.source}"`,
  ]);

  const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
  return csvContent;
};

// Function to handle download
const handleDownload = (results) => {
  const csvData = convertResultsToCSV(results || []);
  if (!results || results.length === 0) {
    alert('No data available to download.');
    return;
  }

  const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'search_results.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};



const Journal_Articles = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Check if user is logged in using localStorage
  const userLoggedIn = localStorage.getItem('token') ? true : false;

  const queryFromUrl = new URLSearchParams(location.search).get('query') || '';
  const filtersFromUrl = Object.fromEntries(
    new URLSearchParams(location.search).entries()
  );

  const isMobile = useMediaQuery('(max-width:768px)'); // Adjust breakpoint as needed
  const [tabValue, setTabValue] = useState(0);
  


  const extractedFilters = {};
  Object.keys(filtersFromUrl).forEach(param => {
    if (param.startsWith('f.')) {
      const facetName = param.replace('f.', '');
      const value = filtersFromUrl[param].split(',')[0]; // Extract value
      extractedFilters[facetName] = value;
    }
  });
  
  const [query, setQuery] = useState(queryFromUrl);



  const [results, setResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false); // State to control modal open/close
  const [selectedArticleId, setSelectedArticleId] = useState(null); // State to store selected article ID
  const [isExpanded, setIsExpanded] = useState(false);  // State for toggling "Read More"
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewMode, setViewMode] = useState('list');
  const [sortOrder, setSortOrder] = useState('relevance');
  const [selectedSort, setSelectedSort] = useState({ field: 'score', direction: 'DESC' });

  // Add state for facets and selected filters
  const [facets, setFacets] = useState({
    openAccess: [],
    publicationYear: [],
    topics: [],
    institutions: [],
    type: []
  });

const [selectedFilters, setSelectedFilters] = useState({});


const [openAddFilterDialog, setOpenAddFilterDialog] = useState(false);



  const theme = useTheme(); // Access the theme

   
  const resultsPerPage = 20; // Define results per page


 

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue); // Change active tab
  };

  
  const truncateText = (text, maxLength) => {
    if (!text || text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
  };

    // Example of `fetchResults` with properly closed braces and commas
    const fetchResults = useCallback(async (searchQuery, pageNumber = 1, filters = {}, sort = 'cited_by_count:desc') => {
      setLoading(true);
      setError(null);
    
      try {
        const filterParams = Object.keys(filters)
          .map((key) => `${key}:${filters[key]}`)
          .join(',');
    
    
          const response = await axios.get('https://api.openalex.org/works', {
          params: {
            filter: `default.search:${searchQuery}${filterParams ? `,${filterParams}` : ''}`,
            sort,
            page: pageNumber,
          },
        });
    
        const items = response.data.results;
    
        setResults(
          items.map((item) => ({
            id: item.id.replace('https://openalex.org/', ''),
            title: item.display_name,
            authors: item.authorships.map((auth) => auth.author.display_name).join(', '),
            year: item.publication_year,
            journal: item.primary_location?.source?.display_name || 'Unknown Journal',
            citations: item.cited_by_count || 0,
            doi: item.doi || null,
            oaStatus: item.open_access?.oa_status || 'Closed',
            source: 'Open Alex',
          }))
        );
        setTotalResults(response.data.meta.count);
      } catch (err) {
        setError('Failed to fetch results.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }, []);
    


    const fetchFacets = useCallback(async (searchQuery, filters = {}) => {
      try {

        const openAlexFilterKeys = {
          topics: 'primary_topic.id',
          institutions: 'authorships.institutions.lineage',
          author: 'authorships.author.id',
          type: 'type',
          source: 'primary_location.source.id',
          openAccess: 'open_access.is_oa',
          publicationYear: 'publication_year'
        };

        // Preparing filters to be included in the API request
        const filterParams = Object.keys(filters)
        .map((key) => {
          const apiKey = openAlexFilterKeys[key] || key;
          return `${apiKey}:${filters[key]}`;
        })
        .join(',');
    
        // Fetching the Open Access facet
        const responseOA = await axios.get('https://api.openalex.org/works', {
          params: {
            filter: `default.search:${searchQuery}${filterParams ? `,${filterParams}` : ''}`,
            group_by: 'open_access.is_oa',
          },
        });
    
        // Fetching the Publication Year facet
        const responseYear = await axios.get('https://api.openalex.org/works', {
          params: {
            filter: `default.search:${searchQuery}${filterParams ? `,${filterParams}` : ''}`,
            group_by: 'publication_year',
          },
        });
    
        // Fetching the Topics facet
        const responseTopic = await axios.get('https://api.openalex.org/works', {
          params: {
            filter: `default.search:${searchQuery}${filterParams ? `,${filterParams}` : ''}`,
            group_by: 'primary_topic.id',
          },
        });

        // Fetching the Institution facet
        const responseInstitution = await axios.get('https://api.openalex.org/works', {
          params: {
            filter: `default.search:${searchQuery}${filterParams ? `,${filterParams}` : ''}`,
            group_by: 'authorships.institutions.lineage',
          },
        });

        // Fetching the Type facet
        const responseType = await axios.get('https://api.openalex.org/works', {
          params: {
            filter: `default.search:${searchQuery}${filterParams ? `,${filterParams}` : ''}`,
            group_by: 'type',
          },
        });

        // Fetching the Source facet
        const responseSource = await axios.get('https://api.openalex.org/works', {
          params: {
            filter: `default.search:${searchQuery}${filterParams ? `,${filterParams}` : ''}`,
            group_by: 'primary_location.source.id',
          },
        });

        // Fetching the Source facet
        const responseAuthor = await axios.get('https://api.openalex.org/works', {
          params: {
            filter: `default.search:${searchQuery}${filterParams ? `,${filterParams}` : ''}`,
            group_by: 'authorships.author.id',
          },
        });
    
        // Saving the response data into state
        setFacets({
          openAccess: responseOA.data.group_by || [],
          publicationYear: responseYear.data.group_by || [],
          topics: responseTopic.data.group_by || [],
          institutions: responseInstitution.data.group_by || [],
          type: responseType.data.group_by || [],
          source: responseSource.data.group_by || [],
          author: responseAuthor.data.group_by || [],
        });
      } catch (err) {
        console.error('Error fetching facets:', err);
        setFacets({
          openAccess: [],
          publicationYear: [],
          topics: [],
          institutions: [],
          type: [],
          source: [],
          author: [],
        });
      }
    }, []);


// 🔁 Unified filter key mapper
const openAlexFilterKeys = {
  topics: 'primary_topic.id',
  institutions: 'authorships.institutions.lineage',
  author: 'authorships.author.id',
  type: 'type',
  source: 'primary_location.source.id',
  openAccess: 'open_access.is_oa',
  publicationYear: 'publication_year'
};

const mapFiltersForAPI = (uiFilters) => {
  const filterParams = [];

  for (const [uiKey, value] of Object.entries(uiFilters)) {
    if (value) {
      const apiKey = openAlexFilterKeys[uiKey] || uiKey;
      filterParams.push(`${apiKey}:${value}`);
    }
  }

  return filterParams.join(',');
};


// ✅ Apply facet
const onFacetChange = (facetName, value) => {
  const updatedFilters = { ...selectedFilters, [facetName]: value };
  setSelectedFilters(updatedFilters);

  const searchParams = new URLSearchParams();
  searchParams.set('filter', mapFiltersForAPI(updatedFilters));
  if (query) searchParams.set('query', query);

  navigate(`/journal_articles?${searchParams.toString()}`);
  fetchResults(query, 1, updatedFilters);
  fetchFacets(query, updatedFilters);
  setPage(1);
};



// ✅ Handle manual filter click (UI or tag click)
const handleFilterClick = (facetKey, facetValue) => {
  const updated = { ...selectedFilters, [facetKey]: facetValue };
  setSelectedFilters(updated);

  const filterString = mapFiltersForAPI(updated);
  const searchParams = new URLSearchParams();
  if (filterString) searchParams.set('filter', filterString);
  if (query) searchParams.set('query', query);

  navigate(`/journal_articles?${searchParams.toString()}`);

  fetchResults(query, 1, updated);
  fetchFacets(query, updated);
  setPage(1);
};

// ✅ Remove a specific filter
const removeFilter = (facetName) => {
  const updated = { ...selectedFilters };
  delete updated[facetName];
  setSelectedFilters(updated);

  const filterString = mapFiltersForAPI(updated);
  const searchParams = new URLSearchParams();
  if (filterString) searchParams.set('filter', filterString);
  if (query) searchParams.set('query', query);

  navigate(`/journal_articles?${searchParams.toString()}`);

  fetchResults(query, 1, updated);
  fetchFacets(query, updated);
  setPage(1);
};

// ✅ Apply filter from dropdown (if used anywhere)
const handleAddFilter = (facetName, value) => {
  handleFilterClick(facetName, value);
};

// ✅ Pagination range helper
const calculateRange = () => {
  const startIndex = (page - 1) * resultsPerPage + 1;
  const endIndex = Math.min(page * resultsPerPage, totalResults);
  return `${startIndex}-${endIndex}`;
};

// ✅ Handle pagination
const handlePageChange = (newPage) => {
  setPage(newPage);

  const filterString = mapFiltersForAPI(selectedFilters);
  const searchParams = new URLSearchParams();
  if (filterString) searchParams.set('filter', filterString);
  if (query) searchParams.set('query', query);

  searchParams.set('spc.page', newPage);

  navigate(`/journal_articles?${searchParams.toString()}`);
  fetchResults(query, newPage, selectedFilters);
};



      const handleSortChange = (newSortOrder) => {
        let sortQueryParam = '';
      
        if (newSortOrder === 'alphabetical') {
          sortQueryParam = 'display_name'; // Alphabetical sort
        } else if (newSortOrder === 'citations') {
          sortQueryParam = 'cited_by_count:desc'; // Sort by citations
        } else if (newSortOrder === 'impact_factor') {
          sortQueryParam = 'cited_by_percentile_year:desc'; // Sort by impact factor
        } else if (newSortOrder === 'publication_year') {
          sortQueryParam = 'publication_year:desc'; // Sort by impact factor
        }
      
        // Update URL
        
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('sort', sortQueryParam);
      
        Object.keys(selectedFilters).forEach((key) => {
          searchParams.set(`f.${key.toLowerCase()}`, selectedFilters[key]);
        });
      
        navigate(`/journal_articles?${searchParams.toString()}`);
      
        // Fetch results immediately
        fetchResults(query, page, selectedFilters, sortQueryParam);
      
        setSortOrder(newSortOrder); // Update state
      };





useEffect(() => {
  if (totalResults > 0) {
    setTotalPages(Math.ceil(totalResults / resultsPerPage));
  }
}, [totalResults, resultsPerPage]);


useEffect(() => {
  const searchParams = new URLSearchParams(location.search);
  const filters = {};
  const filterParam = searchParams.get("filter");

  if (filterParam) {
    filterParam.split(',').forEach(pair => {
      const [key, value] = pair.split(':');
      if (key && value) filters[key] = value;
    });
  }

  setSelectedFilters(filters);
  fetchResults(query, page, filters);
  fetchFacets(query, filters);
}, [location.search, query, page]);





  const SearchResultsWrapper = styled(Box)(({ theme }) => ({
    width: '60%', // Default for larger screens
    paddingLeft: theme.spacing(2),
    minHeight: '500px',
    [theme.breakpoints.down('md')]: {
      width: '100%', // Full width on medium screens or smaller
      paddingLeft: 0,
    },
  }));

  const YouTubeResultsWrapper = styled(Box)(({ theme }) => ({
    width: '40%', // Default for larger screens
    paddingLeft: theme.spacing(2),
    minHeight: '500px',
    [theme.breakpoints.down('md')]: {
      width: '100%', // Full width on medium screens or smaller
      paddingLeft: 0,
    },
  }));


  // Function to handle title click
  const onTitleClick = (result) => {
    trackButtonClick(result.title);
    if (!userLoggedIn) {
      localStorage.setItem('redirectAfterLogin', JSON.stringify(result)); // Store clicked article details
      return;
    }
  else
    {
      navigate(`/journal_articles/${result.id}`);
    }
  };

const trackButtonClick = (buttonName) => {
  console.log(`Button clicked: ${buttonName}`);
  localStorage.setItem("lastButtonClicked", buttonName);
  ReactGA.event({
    category: 'User',
    action: 'Clicked Button or Link',
    label: buttonName,
  });
};







const [articleSource, setArticleSource] = useState(''); // Track article source type



  return (
<div>
  <main className="flex-grow p-4">
    

    {loading && <CircularProgress />}
      {error && (
        <Snackbar open autoHideDuration={6000}>
          <div></div>
        </Snackbar>
      )}      

  <SearchBox_ebrary />
      <br></br>
      {isMobile ? (
          <>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              sx={{ marginBottom: '16px' }}
            >
              <Tab label="Search Results" />
              <Tab label="Search Filters" />
            </Tabs>

            {tabValue === 0 && (
              <Box sx={{ width: '100%', alignItems: 'center', }}>
                <SearchResultsWrapper>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%'
                    }}
                  >
                    <Typography
                      variant="h7"
                      color="primary.main"
                      sx={{ fontWeight: 'bold' }}
                    >
                      {`Showing ${calculateRange()} of ${totalResults.toLocaleString()} journals`}
                    </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <IconButton
                      onClick={() => handleSortChange('citations')}
                      title="Sort by Citations"
                    >
                      <FormatQuoteIcon style={{ marginRight: '8px' }} />
                    </IconButton>

                    <IconButton
                      onClick={() => handleSortChange('alphabetical')}
                      title="Sort Alphabetically"
                    >
                      <SortByAlphaIcon />
                    </IconButton>

                    <IconButton
                      onClick={() => handleSortChange('impact_factor')}
                      title="Sort by Impact Factor"
                    >
                      <ArticleIcon style={{ marginRight: '8px' }} />
                    </IconButton>

                    <IconButton
                      onClick={() => handleSortChange('')}
                      title="Sort by Citation Count"
                    >
                      <TrendingUpIcon style={{ marginRight: '8px' }} />
                    </IconButton>

                    <IconButton onClick={() => handleDownload(filteredResults)}>
                      <DownloadIcon />
                    </IconButton>

                  </Box>
                  

                  {/* Render Search Results */}
                  {results.map((result) => (
                    <Paper
                      key={result.title}
                      sx={{
                        padding: '16px',
                        marginBottom: '12px',
                        borderRadius: '8px',
                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s ease-in-out',
                        width: '92%',
                        border: '1px solid transparent',
                        '&:hover': {
                          backgroundColor: '#f0f8ff',
                          borderColor: '#007bff',
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center">
                        <img
                          src={result.thumbnail || defaultThumbnail}
                          alt="Thumbnail"
                          style={{
                            width: '80px',
                            height: '120px',
                            marginRight: '16px',
                          }}
                        />
                        <Box>
                          <Typography
                            variant="h6"
                            sx={{
                              cursor: 'pointer',
                              color: 'primary.main',
                            }}
                            onClick={() => onTitleClick(result)}
                          >
                            {result.title}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {`Authors: ${result.authors}`}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {`Year: ${result.year}`} | {`Published by: ${
                              result.publisher || 'N/A'
                            }`}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {`Subject: ${result.subject || 'N/A'}`}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                  <Pagination
                    page={page}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </SearchResultsWrapper>
              </Box>
            )}

            {tabValue === 1 && (

            <Box sx={{ 
              width: '98%',
              marginLeft: '5px',
              alignItems: 'center',
              }}
            >
                <YouTubeResultsWrapper>
                  <Paper
                    elevation={0}
                    sx={{
                      marginTop: '40px',
                      backgroundColor: '#f2f9ff',
                    }}
                  >

                  <Paper
                      sx={{
                        padding: '16px',
                        marginBottom: '12px',
                        borderRadius: '8px',
                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s ease-in-out', // Smooth transition
                        border: '1px solid transparent', // Default border
                        '&:hover': {
                          backgroundColor: '#f0f8ff', // Change to your desired hover color
                          borderColor: '#007bff', // Optional: Change border color on hover
                        },
                      }}
                    >
                  <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold', marginBottom: '16px' }}>
                    {totalResults.toLocaleString()} Journals
                  </Typography>
                  </Paper>



                </Paper>
                </YouTubeResultsWrapper>
              </Box>
            )}
          </>
        ) : (


      <Box className="main-content" display="flex">
        {/* Left Section: Search Results */}
        <SearchResultsWrapper>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                whiteSpace: 'nowrap',
                paddingBottom: '2px',
                paddingTop: '20px',
                gap: '2px',
                  }}
              >
            <Typography variant="h7" color="primary.main" sx={{ fontWeight: 'bold' }}>
              {`Showing ${calculateRange()} of ${totalResults.toLocaleString()} journal articles`}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        
        <IconButton
          onClick={() => handleSortChange('citations')}
          title="Sort by Citations"
        >
          <FormatQuoteIcon style={{ marginRight: '8px' }} />
        </IconButton>

        <IconButton
          onClick={() => handleSortChange('alphabetical')}
          title="Sort Alphabetically"
        >
          <SortByAlphaIcon />
        </IconButton>

        <IconButton
          onClick={() => handleSortChange('impact_factor')}
          title="Sort by Impact Factor"
        >
          <TrendingUpIcon style={{ marginRight: '8px' }} />
        </IconButton>

        <IconButton
          onClick={() => handleSortChange('publication_year')}
          title="Sort by Latest"
        >
          <ArticleIcon style={{ marginRight: '8px' }} />
        </IconButton>

        <IconButton onClick={() => handleDownload(results)}>
        <DownloadIcon />
        </IconButton>

      </Box>
    </Box>

    {/* Render Search Results */}
    {results.map((result) => (
      <Paper
        key={result.title}
        sx={{
          padding: '16px',
          marginBottom: '12px',
          marginTop: '12px',
          borderRadius: '8px',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s ease-in-out', // Smooth transition
          border: '1px solid transparent', // Default border
          '&:hover': {
            backgroundColor: '#f0f8ff', // Change to your desired hover color
            borderColor: '#007bff', // Optional: Change border color on hover
          },
        }}
      >
        <Box display="flex" alignItems="center">
          <img
            src={result.thumbnail || defaultThumbnail}
            alt="Thumbnail"
            style={{ 
              width: '80px', 
              height: '80px', 
              marginRight: '16px',
            }}
          />
          <Box>
          <Typography
              variant="h6"
              sx={{
                cursor: 'pointer', // Ensures the pointer cursor is shown
                color: 'primary.main', // Use Material-UI's primary color
              }}
              onClick={() => onTitleClick(result)}
            >
              {result.title}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {`Authors: ${result.authors}`}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {result.year || 'N/A'}, {`Journal: ${result.journal || 'N/A'}`}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {result.oaStatus || 'N/A'}, {`DOI: ${result.doi || 'N/A'}`}, <b>{`Citation Count: ${result.citations || 'N/A'}`}</b>
            </Typography>
          </Box>
        </Box>
      </Paper>
    ))}
    <Pagination page={page} totalPages={totalPages} onPageChange={handlePageChange} />
    
  </SearchResultsWrapper>

  <YouTubeResultsWrapper>


<Paper
        sx={{
          padding: '16px',
          marginBottom: '12px',
          borderRadius: '8px',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s ease-in-out', // Smooth transition
          border: '1px solid transparent', // Default border
          '&:hover': {
            backgroundColor: '#f0f8ff', // Change to your desired hover color
            borderColor: '#007bff', // Optional: Change border color on hover
          },
        }}
      >
    <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold', marginBottom: '16px' }}>
      {totalResults.toLocaleString()} Journal Articles
    </Typography>
    </Paper>

      <Box
        sx={{
          width: '98%',
          margin: '0 auto', // Centers the box horizontally
          textAlign: 'center', // Centers text inside the box
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" sx={{ marginBottom: '8px' }}>
          
        </Typography>
        <FilterTags1
        selectedFilters={selectedFilters}
        handleAddFilter={handleAddFilter}
        removeFilter={removeFilter}
        facets={facets} // Updated facets based on the current search query
      />
      </Box>





      <Paper sx={{ padding: '16px', marginBottom: '12px' }}>
  <a
    href="#"
    onClick={() => handleFilterClick('is_oa', 'true')}
    style={{ textDecoration: 'none', color: 'inherit' }}
  >
    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
      Fully Open Access
    </Typography>

    {/* Check if facets.openAccess is available and has the correct data */}
    {facets.openAccess && facets.openAccess.length > 0 ? (
      <Typography variant="h4" color="primary">
        {(
          (facets.openAccess.find((facet) => facet.key === '1')?.count || 0) /
          totalResults *
          100
        ).toFixed(1)}% 
      </Typography>
    ) : (
      <Typography variant="body2">No data available</Typography>
    )}

    <Typography variant="body2" color="textSecondary">
      {Math.round(
        facets.openAccess.find((facet) => facet.key === '1')?.count || 0
      ).toLocaleString()} journal articles
    </Typography>
  </a>
</Paper>

    <FacetArticles
      facets={facets}
      selectedFacets={selectedFilters}
      onFacetChange={onFacetChange}
    />




</YouTubeResultsWrapper>





 
        </Box>
)}
    </main>
    </div>
  );
};

Journal_Articles.propTypes = {
  query: PropTypes.string.isRequired,
};

export default Journal_Articles;
